import moment from 'moment-timezone';
import { createReducer } from 'redux-act';
import { v4 } from 'uuid';

import { AnnouncementType } from '../../models/AnnouncementType';
import { Announcement } from '../../models/interfaces/Announcement';
import ReservationRejectReason from '../../models/interfaces/reservation/ReservationRejectReason';
import { announcementAcknowledged, AnnouncementAcknowledgedPayload } from '../actions/announcement/announcementAcknowledged';
import { authenticationRequired } from '../actions/authentication/authenticationRequired';
import { logout } from '../actions/authentication/logout';
import { feedbackFailed, FeedbackFailedPayload, feedbackReceived, FeedbackReceivedPayload } from '../actions/feedback/sendFeedback';
import { layoutSaved, LayoutSavedPayload } from '../actions/layout/saveLayout';
import {
  reservationCanceled,
  ReservationCanceledPayload,
  reservationCancelFailed,
  ReservationCancelFailedPayload,
} from '../actions/reservation/cancelReservation';
import {
  reservationCreated,
  ReservationCreatedPayload,
  reservationCreationFailed,
  ReservationCreationFailedPayload,
} from '../actions/reservation/createReservation';
import { guestArrivedFailed, GuestArrivedFailedPayload, guestArrivedSaved, GuestArrivedSavedPayload } from '../actions/reservation/guestArrived';
import {
  reservationUpdated,
  ReservationUpdatedPayload,
  reservationUpdateFailed,
  ReservationUpdateFailedPayload,
} from '../actions/reservation/updateReservation';
import {
  reservationStateUpdated,
  ReservationStateUpdatedPayload,
  reservationStateUpdateFailed,
  ReservationStateUpdateFailedPayload,
} from '../actions/reservation/updateReservationState';
import { employeeModified, employeeModifyFailed } from '../actions/setting/company/modifyEmployee';
import { contactSaved, ContactSavedPayload } from '../actions/setting/saveContact';
import { profileSaved, ProfileSavedPayload } from '../actions/setting/saveProfile';
import { reservationSettingSaved, ReservationSettingSavedPayload } from '../actions/setting/saveReservationSetting';

export type AnnouncementState = Announcement[];

const defaultState: AnnouncementState = [];

const announcementReducer = createReducer<typeof defaultState>({}, defaultState);

function createAnnouncement(announcementType: AnnouncementType, timestamp: moment.Moment): Announcement {
  const announcement = {
    uuid: v4(),
    announcementType,
    timestamp,
    shown: false,
  };
  return announcement;
}

announcementReducer.on(announcementAcknowledged, (announcementState, { uuid: shownUuid }: AnnouncementAcknowledgedPayload) =>
  announcementState.map((announcement) => (announcement.uuid === shownUuid ? { ...announcement, shown: true } : announcement))
);

announcementReducer.on(reservationCreated, (announcementState, { timestamp }: ReservationCreatedPayload) => [
  ...announcementState,
  createAnnouncement(AnnouncementType.ReservationCreated, timestamp),
]);
announcementReducer.on(reservationCreationFailed, (announcementState, { timestamp, rejectReason }: ReservationCreationFailedPayload) => [
  ...announcementState,
  createAnnouncement(
    rejectReason === ReservationRejectReason.OutsideHours ? AnnouncementType.ReservationOutsideHours : AnnouncementType.ReservationCreationFailed,
    timestamp
  ),
]);
announcementReducer.on(reservationUpdated, (announcementState, { timestamp }: ReservationUpdatedPayload) => [
  ...announcementState,
  createAnnouncement(AnnouncementType.ReservationUpdated, timestamp),
]);
announcementReducer.on(reservationUpdateFailed, (announcementState, { timestamp }: ReservationUpdateFailedPayload) => [
  ...announcementState,
  createAnnouncement(AnnouncementType.ReservationUpdateFailed, timestamp),
]);
announcementReducer.on(reservationCanceled, (announcementState, { timestamp }: ReservationCanceledPayload) => [
  ...announcementState,
  createAnnouncement(AnnouncementType.ReservationCanceled, timestamp),
]);
announcementReducer.on(reservationCancelFailed, (announcementState, { timestamp }: ReservationCancelFailedPayload) => [
  ...announcementState,
  createAnnouncement(AnnouncementType.ReservationCancelFailed, timestamp),
]);
announcementReducer.on(guestArrivedSaved, (announcementState, { timestamp }: GuestArrivedSavedPayload) => [
  ...announcementState,
  createAnnouncement(AnnouncementType.ReservationStateUpdated, timestamp),
]);
announcementReducer.on(guestArrivedFailed, (announcementState, { timestamp }: GuestArrivedFailedPayload) => [
  ...announcementState,
  createAnnouncement(AnnouncementType.ReservationStateUpdateFailed, timestamp),
]);
announcementReducer.on(reservationStateUpdated, (announcementState, { timestamp }: ReservationStateUpdatedPayload) => [
  ...announcementState,
  createAnnouncement(AnnouncementType.ReservationStateUpdated, timestamp),
]);
announcementReducer.on(reservationStateUpdateFailed, (announcementState, { timestamp }: ReservationStateUpdateFailedPayload) => [
  ...announcementState,
  createAnnouncement(AnnouncementType.ReservationStateUpdateFailed, timestamp),
]);
announcementReducer.on(feedbackReceived, (announcementState, { timestamp }: FeedbackReceivedPayload) => [
  ...announcementState,
  createAnnouncement(AnnouncementType.FeedbackReceived, timestamp),
]);
announcementReducer.on(feedbackFailed, (announcementState, { timestamp }: FeedbackFailedPayload) => [
  ...announcementState,
  createAnnouncement(AnnouncementType.FeedbackFailed, timestamp),
]);
announcementReducer.on(contactSaved, (announcementState, { timestamp }: ContactSavedPayload) => [
  ...announcementState,
  createAnnouncement(AnnouncementType.ContactSaved, timestamp),
]);
announcementReducer.on(reservationSettingSaved, (announcementState, { timestamp }: ReservationSettingSavedPayload) => [
  ...announcementState,
  createAnnouncement(AnnouncementType.ReservationSettingSaved, timestamp),
]);
announcementReducer.on(profileSaved, (announcementState, { timestamp }: ProfileSavedPayload) => [
  ...announcementState,
  createAnnouncement(AnnouncementType.ProfileSaved, timestamp),
]);
announcementReducer.on(layoutSaved, (announcementState, { timestamp }: LayoutSavedPayload) => [
  ...announcementState,
  createAnnouncement(AnnouncementType.LayoutSaved, timestamp),
]);
announcementReducer.on(employeeModified, (announcementState, { timestamp }) => [
  ...announcementState,
  createAnnouncement(AnnouncementType.EmployeeUpdated, timestamp),
]);
announcementReducer.on(employeeModifyFailed, (announcementState, { timestamp }) => [
  ...announcementState,
  createAnnouncement(AnnouncementType.EmployeeUpdateFailed, timestamp),
]);

announcementReducer.on(logout, () => defaultState);

announcementReducer.on(authenticationRequired, () => defaultState);

export default announcementReducer;
